/**
 * LandingPage
 * ============
 * Public marketing landing for un-authenticated visitors at `/`.
 *
 * Built per /app/design_guidelines.md:
 *   - Marketing theme tokens (data-theme="marketing")
 *   - Figtree font, deep teal hsl(189 53% 25%) primary
 *   - Shadcn Button + Card + Accordion + Badge
 *   - lucide-react icons (no emoji)
 *   - Sections (in order): Navbar, Hero, Social proof, Benefits bento,
 *     Feature spotlights, Pricing teaser, Testimonials, FAQ, Final CTA,
 *     Footer, Sticky mobile CTA.
 *   - Every interactive + critical info element has a kebab-case
 *     data-testid (per design + project rules).
 *
 * Routing: rendered from App.js when `!user`. Authed users still see
 * their dashboard at `/`. Pricing/login routes are unchanged.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { analytics, EVENTS } from '../utils/analytics';
import { DemoRequestModal } from '../components/DemoRequestModal';
import {
  ShieldCheck, Sparkles, Video, Building2, BadgeCheck,
  CalendarDays, CreditCard, Camera, Play, ArrowRight, Check,
  Star, Quote, Menu, X, Heart, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { SEO } from '../components/SEO';

// ============================================================
// Brand "W" mark — same artwork as /public/favicon.svg.
// Keeping it as an inline React component lets us colour it via
// currentColor when needed and avoids an extra HTTP request.
// ============================================================
const BrandLogo = ({ size = 36, withRing = true, ariaLabel = 'Kids WiseApp' }) => (
  <svg
    role="img"
    aria-label={ariaLabel}
    width={size}
    height={size}
    viewBox="0 0 100 100"
    style={{ display: 'block', flexShrink: 0 }}
  >
    <defs>
      <linearGradient id="kwBrandLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00abc9" />
        <stop offset="100%" stopColor="#2abb9b" />
      </linearGradient>
    </defs>
    {withRing && <circle cx="50" cy="50" r="48" fill="url(#kwBrandLogoGrad)" />}
    <path
      d="M 20 30 L 28 60 L 35 40 L 42 60 L 50 30 L 58 60 L 65 40 L 72 60 L 80 30"
      stroke={withRing ? '#ffffff' : 'url(#kwBrandLogoGrad)'}
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <circle cx="75" cy="25" r="6" fill="#FFB84D" />
    <circle cx="73" cy="23" r="2" fill="#ffffff" opacity="0.8" />
  </svg>
);

// ============================================================
// SECTION: Navbar
// ============================================================

const NAV_LINKS = [
  { label: 'Why', href: '#why', testid: 'landing-nav-link-why' },
  { label: 'Features', href: '#features', testid: 'landing-nav-link-features' },
  { label: 'Pricing', href: '/pricing', external: true, testid: 'landing-nav-link-pricing' },
  { label: 'FAQ', href: '#faq', testid: 'landing-nav-link-faq' },
];

const Navbar = ({ onTrialClick, onPricingClick, onDemoClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="landing-navbar"
      style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: scrolled ? 'rgba(246,251,250,0.85)' : 'rgba(246,251,250,0.6)',
        backdropFilter: 'saturate(140%) blur(10px)',
        WebkitBackdropFilter: 'saturate(140%) blur(10px)',
        borderBottom: `1px solid ${scrolled ? 'var(--kw-border)' : 'transparent'}`,
        boxShadow: scrolled ? 'var(--kw-shadow-sm)' : 'none',
        transition: 'background-color 220ms ease, border-color 220ms ease, box-shadow 220ms ease',
      }}
    >
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        padding: '0 24px',
        height: scrolled ? 60 : 72,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        transition: 'height 220ms ease',
      }}>
        {/* Brand */}
        <Link to="/" data-testid="landing-nav-brand" style={brandStyle}>
          <BrandLogo size={36} />
          <span style={brandWordmarkStyle}>
            Kids <span className="kw-gradient-text">WiseApp</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="kw-hide-mobile" style={navLinksStyle}>
          {NAV_LINKS.map((l) => (
            l.external ? (
              <Link key={l.href} to={l.href} data-testid={l.testid} style={navLinkStyle}>{l.label}</Link>
            ) : (
              <a key={l.href} href={l.href} data-testid={l.testid} style={navLinkStyle}>{l.label}</a>
            )
          ))}
        </nav>

        {/* Desktop CTA group */}
        <div className="kw-hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/login" data-testid="landing-nav-signin-link" style={signinLinkStyle}>Sign in</Link>
          <Button
            variant="outline"
            onClick={onPricingClick}
            data-testid="landing-navbar-see-pricing-button"
            className="kw-focus-ring"
            style={{
              borderColor: 'var(--kw-border-strong)',
              color: 'var(--kw-text)',
              background: '#fff',
              fontWeight: 600,
              borderRadius: 12,
              padding: '8px 16px',
            }}
          >
            See pricing
          </Button>
          {/* Book a demo — deliberately outline styling so the primary
              "Start free trial" CTA still owns the loudest pixel budget.
              This button caters to owners who want to talk to a human
              first before self-serving a trial. */}
          <Button
            variant="outline"
            onClick={onDemoClick}
            data-testid="landing-navbar-book-demo-button"
            className="kw-focus-ring"
            style={{
              borderColor: 'var(--kw-border)',
              color: 'var(--kw-teal-700)',
              fontWeight: 600,
              borderRadius: 12,
              padding: '8px 14px',
              background: 'rgba(255,255,255,0.55)',
            }}
          >
            Book a demo
          </Button>
          <Button
            onClick={onTrialClick}
            data-testid="landing-navbar-start-trial-button"
            className="kw-focus-ring"
            style={{
              background: 'var(--kw-gradient-brand)',
              color: '#fff',
              fontWeight: 700,
              border: 'none',
              borderRadius: 12,
              padding: '8px 18px',
              boxShadow: '0 10px 22px rgba(0,171,201,0.25)',
            }}
          >
            Start free trial
          </Button>
        </div>

        {/* Mobile hamburger */}
        <div className="kw-show-mobile" style={{ display: 'none' }}>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open menu"
                data-testid="landing-navbar-mobile-menu-button"
                style={mobileMenuBtnStyle}
              >
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" style={{ background: 'var(--kw-bg)' }} data-testid="landing-navbar-mobile-sheet">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 24 }}>
                {NAV_LINKS.map((l) => (
                  l.external ? (
                    <Link key={l.href} to={l.href} onClick={() => setOpen(false)} data-testid={`${l.testid}-mobile`} style={mobileLinkStyle}>{l.label}</Link>
                  ) : (
                    <a key={l.href} href={l.href} onClick={() => setOpen(false)} data-testid={`${l.testid}-mobile`} style={mobileLinkStyle}>{l.label}</a>
                  )
                ))}
                <Link to="/login" onClick={() => setOpen(false)} data-testid="landing-nav-signin-link-mobile" style={mobileLinkStyle}>Sign in</Link>
                <Button onClick={() => { setOpen(false); onTrialClick(); }} data-testid="landing-navbar-start-trial-button-mobile" style={{ background: 'var(--kw-gradient-brand)', color: '#fff', fontWeight: 700, marginTop: 8, border: 'none', borderRadius: 12 }}>
                  Start free trial
                </Button>
                <Button variant="outline" onClick={() => { setOpen(false); onPricingClick(); }} data-testid="landing-navbar-see-pricing-button-mobile" style={{ borderColor: 'var(--kw-border-strong)', color: 'var(--kw-text)', fontWeight: 600, borderRadius: 12 }}>
                  See pricing
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

// ============================================================
// SECTION: Hero
// ============================================================

const Hero = ({ onTrialClick, onPricingClick, onDemoClick }) => (
  <section
    data-testid="landing-hero"
    style={{
      position: 'relative',
      padding: '64px 24px 96px',
      overflow: 'hidden',
    }}
  >
    {/* Layered decorative gradient orbs — adds depth without dominating */}
    <div aria-hidden style={{
      position: 'absolute', inset: 0,
      background: 'var(--kw-gradient-hero)',
      pointerEvents: 'none',
    }} />
    {/* Subtle dotted grain overlay for premium texture */}
    <div aria-hidden style={{
      position: 'absolute', inset: 0,
      backgroundImage: 'radial-gradient(circle, rgba(10,31,36,0.06) 1px, transparent 1px)',
      backgroundSize: '22px 22px',
      opacity: 0.35,
      maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 75%)',
      WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 75%)',
      pointerEvents: 'none',
    }} />

    <div style={{
      position: 'relative',
      maxWidth: 1200, margin: '0 auto',
      display: 'grid', gridTemplateColumns: '1fr', gap: 56,
      alignItems: 'center',
    }} className="kw-hero-grid">
      {/* Copy column */}
      <div className="kw-fade-up">
        <div data-testid="landing-hero-eyebrow-row" style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '6px 6px 6px 10px',
          background: '#ffffff',
          border: '1px solid var(--kw-border)',
          borderRadius: 999,
          boxShadow: 'var(--kw-shadow-sm)',
          marginBottom: 22,
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--kw-text-soft)' }}>
            ✨ New · Event Recap Videos
          </span>
          <span style={{
            fontSize: 11, fontWeight: 800,
            color: '#fff',
            background: 'var(--kw-gradient-brand)',
            padding: '4px 10px',
            borderRadius: 999,
            letterSpacing: '0.04em',
          }}>
            BETA
          </span>
        </div>

        <h1 data-testid="landing-hero-headline" style={h1Style}>
          The childcare platform that<br/>
          <span className="kw-gradient-text">parents adore</span> &amp;<br/>
          staff trust.
        </h1>
        <p data-testid="landing-hero-subheadline" style={subStyle}>
          Safeguarding-first sign-in/out, parent Moments, cinematic recap
          videos and Mollie-powered billing — purpose-built for UK
          nurseries, pre-schools and childminders.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 30 }}>
          <Button
            onClick={onTrialClick}
            data-testid="landing-hero-start-trial-button"
            className="kw-focus-ring"
            style={primaryBtnLg}
          >
            Start free 14-day trial
            <ArrowRight size={18} style={{ marginLeft: 8 }} />
          </Button>
          <Button
            variant="outline"
            onClick={onPricingClick}
            data-testid="landing-hero-see-pricing-button"
            className="kw-focus-ring"
            style={secondaryBtnLg}
          >
            See pricing
          </Button>
          <Button
            variant="ghost"
            onClick={onDemoClick}
            data-testid="landing-hero-book-demo-button"
            className="kw-focus-ring"
            style={{ ...secondaryBtnLg, background: 'transparent', border: '1px dashed var(--kw-border)' }}
          >
            📅 Book a 15-min demo
          </Button>
        </div>
        <p data-testid="landing-hero-microcopy" style={microcopyStyle}>
          No card required to start · Cancel anytime · GDPR-ready
        </p>
        {/* Trust bullets */}
        <ul style={trustBulletsStyle} data-testid="landing-hero-trust-bullets">
          {[
            { t: '14-day free trial', icon: BadgeCheck },
            { t: 'Cancel anytime', icon: Check },
            { t: '£ UK pricing', icon: Heart },
            { t: 'GDPR-ready', icon: ShieldCheck },
          ].map(({ t, icon: Icon }) => (
            <li key={t} style={trustBulletItem}>
              <span style={trustBulletIcon}><Icon size={12} strokeWidth={2.5} /></span>
              {t}
            </li>
          ))}
        </ul>
      </div>

      {/* Visual column — stylised browser frame mockup */}
      <div className="kw-hide-mobile-flex" style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
        {/* Floating brand logo orb behind the mockup for depth */}
        <div aria-hidden className="kw-float" style={{
          position: 'absolute', top: -10, right: 8, zIndex: 1,
          width: 78, height: 78, borderRadius: '50%',
          background: '#fff',
          boxShadow: 'var(--kw-shadow-brand)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 10,
        }}>
          <BrandLogo size={58} />
        </div>
        <BrowserMockup />
      </div>
    </div>
  </section>
);

const BrowserMockup = () => (
  <div data-testid="landing-hero-browser-frame" style={browserFrameStyle}>
    <div style={browserChromeStyle}>
      <span style={{ ...browserDot, background: '#ff5f57' }} />
      <span style={{ ...browserDot, background: '#febc2e' }} />
      <span style={{ ...browserDot, background: '#28c840' }} />
      <span style={browserAddressBar}>kidswiseapp.com</span>
    </div>
    <div style={browserContentStyle}>
      <div style={mockSidebarStyle}>
        <div style={mockSidebarBrand}>
          <BrandLogo size={18} />
          <span>Kids WiseApp</span>
        </div>
        {['Overview', 'Daily Roster', 'Events', 'Moments', 'Billing'].map((m, i) => (
          <div key={m} style={{ ...mockSidebarItem, ...(i === 1 ? mockSidebarItemActive : {}) }}>{m}</div>
        ))}
      </div>
      <div style={mockMainStyle}>
        <div style={mockHeaderRow}>
          <div>
            <div style={mockH2}>Daily Roster</div>
            <div style={mockSubtle}>Today’s check-ins, sign-outs &amp; ratios</div>
          </div>
          <div style={mockBadgeOk}>
            <ShieldCheck size={12} style={{ marginRight: 4 }} /> Ratios OK
          </div>
        </div>
        <div style={mockStatsRow}>
          {[{ n: 28, l: 'Children in', c: '#0b6b58' }, { n: 6, l: 'Awaiting', c: '#7a5a13' }, { n: 12, l: 'Departed', c: '#446f6b' }].map((s) => (
            <div key={s.l} style={mockStatCard}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.c }}>{s.n}</div>
              <div style={mockStatLabel}>{s.l}</div>
            </div>
          ))}
        </div>
        {[
          { name: 'Olivia P.', meta: 'Room 2 · Signed in 09:02', status: 'in' },
          { name: 'Theo R.', meta: 'Room 1 · Signed in 08:55', status: 'in' },
          { name: 'Emma S.', meta: 'Room 3 · Departed 13:10', status: 'out' },
        ].map((c) => (
          <div key={c.name} style={mockRosterRow}>
            <div style={mockAvatar}>{c.name[0]}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--kw-text)' }}>{c.name}</div>
              <div style={{ fontSize: 10, color: 'var(--kw-muted)' }}>{c.meta}</div>
            </div>
            <span style={c.status === 'in' ? mockPillIn : mockPillOut}>
              {c.status === 'in' ? 'In' : 'Out'}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// SECTION: Social proof strip
// ============================================================

const SocialProof = () => (
  <section
    data-testid="landing-social-proof-strip"
    style={{
      padding: '32px 24px',
      borderTop: '1px solid var(--kw-border)',
      borderBottom: '1px solid var(--kw-border)',
      background: 'rgba(255,255,255,0.6)',
    }}
  >
    <div style={{
      maxWidth: 1200, margin: '0 auto',
      display: 'flex', flexDirection: 'column', gap: 18,
      alignItems: 'center', textAlign: 'center',
    }}>
      <div data-testid="landing-social-proof-metric" style={{ fontSize: 12, fontWeight: 700, color: 'var(--kw-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Trusted by 120+ UK childcare settings
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, justifyContent: 'center', opacity: 0.7 }}>
        {['Little Acorns', 'Sunshine Nursery', 'Oakwood Pre-School', 'Maple Tree Childminders', 'Brookside Daycare', 'Riverbend Group'].map((n) => (
          <div key={n} style={{
            fontFamily: "'Figtree', sans-serif",
            fontWeight: 700, fontSize: 14,
            color: 'var(--kw-muted)',
            padding: '6px 10px',
            border: '1px dashed var(--kw-border)',
            borderRadius: 8,
          }}>{n}</div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================
// SECTION: Benefits bento grid
// ============================================================

const BENEFITS = [
  { Icon: ShieldCheck, title: 'Safeguarding-first sign-in/out', desc: 'Fast drop-off and pick-up with clear, GDPR-friendly audit trails.', testid: 'landing-benefit-safeguarding-card', wide: true },
  { Icon: Sparkles, title: 'Parent app + Moments', desc: 'Share photos and updates that parents actually read.', testid: 'landing-benefit-moments-card' },
  { Icon: Video, title: 'Event recap videos', desc: 'Auto-edited highlights with no-login share links.', testid: 'landing-benefit-recap-card' },
  { Icon: Building2, title: 'Multi-site + payroll', desc: 'Manage rotas, staff, and costs across centres.', testid: 'landing-benefit-multisite-card' },
];

const BenefitsBento = () => (
  <section id="why" data-testid="landing-benefits-section" style={{ padding: '80px 24px', background: 'var(--kw-bg)' }}>
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 36 }}>
        <span style={sectionEyebrow}>Why Kids WiseApp</span>
        <h2 data-testid="landing-benefits-title" style={sectionTitleStyle}>
          One platform built specifically for UK childcare settings.
        </h2>
        <p style={sectionLeadStyle}>
          From single-site childminders to multi-site nursery groups — replace the
          spreadsheets, paper folders and disconnected apps.
        </p>
      </div>
      <div className="kw-benefits-grid">
        {BENEFITS.map(({ Icon, title, desc, testid, wide }) => (
          <article
            key={testid}
            data-testid={testid}
            className="kw-hover-lift"
            style={{
              ...benefitCardStyle,
              gridColumn: wide ? 'span 2' : 'span 1',
              background: wide ? 'linear-gradient(180deg, #ffffff, #f4fafa)' : '#ffffff',
            }}
          >
            <div style={benefitIconWrap}>
              <Icon size={20} />
            </div>
            <h3 style={benefitTitleStyle}>{title}</h3>
            <p style={benefitDescStyle}>{desc}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================
// SECTION: Feature spotlights (alternating)
// ============================================================

const FEATURES = [
  {
    id: 'daily-roster',
    eyebrow: 'Daily roster',
    title: 'A roster staff can run in seconds.',
    bullets: [
      'Quick room moves and live ratios',
      'Attendance overview at a glance',
      'Designed for non-technical teams',
    ],
    Icon: CalendarDays,
    accent: 'rgba(201,238,236,0.55)',
    testid: 'landing-feature-daily-roster',
  },
  {
    id: 'parent-moments',
    eyebrow: 'Parent app & Moments',
    title: 'Moments that build parent trust.',
    bullets: [
      'Photo updates with context and tagging',
      'Consent-aware sharing',
      'A calm, parent-friendly feed',
    ],
    Icon: Camera,
    accent: 'rgba(255, 234, 200, 0.7)',
    testid: 'landing-feature-parent-moments',
  },
  {
    id: 'event-recap',
    eyebrow: 'Event recap videos',
    title: 'Cinematic recap videos — without the editing.',
    bullets: [
      'Auto-edited slideshows with music',
      'No-login share links for families',
      'Perfect for open days and trips',
    ],
    Icon: Play,
    accent: 'rgba(201,238,236,0.7)',
    testid: 'landing-feature-event-recap',
  },
  {
    id: 'billing-payroll',
    eyebrow: 'Smart billing + payroll',
    title: 'Billing and payroll that stay in sync.',
    bullets: [
      'Card &amp; BACS Direct Debit subscriptions for your families',
      'Clear invoices and CSV exports',
      'Multi-site reporting and approvals',
    ],
    Icon: CreditCard,
    accent: 'rgba(255, 234, 200, 0.55)',
    testid: 'landing-feature-billing',
  },
];

const FeatureSpotlight = ({ feature, index }) => {
  const reverse = index % 2 === 1;
  const { Icon, eyebrow, title, bullets, accent, testid } = feature;
  return (
    <article
      id={feature.id}
      data-testid={testid}
      style={{
        display: 'grid', gridTemplateColumns: '1fr',
        gap: 40, alignItems: 'center',
        marginBottom: 64,
      }}
      className="kw-feature-grid"
    >
      <div style={{ order: reverse ? 2 : 1 }} className="kw-fade-up">
        <span style={sectionEyebrow}>{eyebrow}</span>
        <h3 style={featureTitleStyle}>{title}</h3>
        <ul style={featureBulletListStyle}>
          {bullets.map((b) => (
            <li key={b} style={featureBulletItemStyle}>
              <span style={featureBulletCheckStyle}><Check size={13} strokeWidth={3} /></span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div style={{ order: reverse ? 1 : 2 }}>
        <div data-testid={`${testid}-image`} style={{
          ...featureVisualStyle,
          background: `linear-gradient(135deg, ${accent}, rgba(255,255,255,0.4))`,
        }}>
          <div style={featureVisualInner}>
            <div style={featureVisualIconWrap}>
              <Icon size={36} strokeWidth={1.8} />
            </div>
            <div style={featureVisualLabel}>{eyebrow}</div>
          </div>
        </div>
      </div>
    </article>
  );
};

const FeatureSpotlights = ({ onPricingClick }) => (
  <section id="features" data-testid="landing-features-section" style={{ padding: '80px 24px', background: '#ffffff' }}>
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 56 }}>
        <span style={sectionEyebrow}>How it works</span>
        <h2 data-testid="landing-features-title" style={sectionTitleStyle}>
          The all-in-one toolkit your setting actually uses every day.
        </h2>
      </div>

      {FEATURES.map((f, i) => (
        <React.Fragment key={f.id}>
          <FeatureSpotlight feature={f} index={i} />
          {/* Inline CTA after the second spotlight */}
          {i === 1 && (
            <div style={inlineCtaBlockStyle}>
              <p style={{ margin: 0, fontWeight: 600, color: 'var(--kw-text)' }}>
                Curious about plans for your setting?
              </p>
              <Button
                variant="link"
                onClick={onPricingClick}
                data-testid="landing-feature-inline-see-pricing-link"
                style={{ color: 'var(--kw-cyan)', fontWeight: 700, padding: 0 }}
              >
                See pricing for UK plans
                <ChevronRight size={16} style={{ marginLeft: 4 }} />
              </Button>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  </section>
);

// ============================================================
// SECTION: Pricing teaser (links to full /pricing page)
// ============================================================

const PRICING_TIERS = [
  { name: 'Starter',     price: '£29',   highlights: ['Up to 25 children', 'Parent Moments', 'Email support'], testid: 'landing-pricing-teaser-starter' },
  { name: 'Growth',      price: '£69',   highlights: ['Up to 80 children', 'Events + recap videos', 'Priority support'], testid: 'landing-pricing-teaser-growth', recommended: true },
  { name: 'Professional', price: '£129', highlights: ['Up to 200 children', 'Multi-site + payroll', 'Phone support'], testid: 'landing-pricing-teaser-pro' },
  { name: 'Enterprise',  price: 'from £249', highlights: ['Unlimited capacity', 'Dedicated CSM + SLA', 'Bespoke onboarding'], testid: 'landing-pricing-teaser-enterprise' },
];

const PricingTeaser = ({ onTrialClick, onPricingClick, onDemoClick }) => (
  <section id="pricing" data-testid="landing-pricing-section" style={{ padding: '80px 24px', background: 'var(--kw-bg)' }}>
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 36 }}>
        <span style={sectionEyebrow}>Pricing</span>
        <h2 data-testid="landing-pricing-title" style={sectionTitleStyle}>
          Simple plans that scale with your setting.
        </h2>
        <p style={sectionLeadStyle}>
          Starter <strong>£29</strong> → Enterprise <strong>from £249</strong>, billed monthly.
          14-day free trial on Starter, Growth and Professional. Cancel anytime.
        </p>
      </div>
      <div className="kw-pricing-grid">
        {PRICING_TIERS.map((t) => (
          <article
            key={t.testid}
            data-testid={t.testid}
            className="kw-hover-lift"
            style={{
              ...pricingCardStyle,
              ...(t.recommended ? pricingCardRecommendedStyle : {}),
            }}
          >
            {t.recommended && (
              <span data-testid={`${t.testid}-recommended-badge`} style={recommendedBadge}>
                <Star size={11} style={{ marginRight: 4 }} /> Most popular
              </span>
            )}
            <div style={pricingTierName}>{t.name}</div>
            <div style={pricingTierPrice}>
              {t.price}
              {!t.price.startsWith('from') && <span style={pricingTierPriceSuffix}> /mo</span>}
            </div>
            <ul style={pricingTierHighlightList}>
              {t.highlights.map((h) => (
                <li key={h} style={pricingTierHighlightItem}>
                  <Check size={14} style={{ color: 'var(--kw-cyan)', marginRight: 8, flexShrink: 0 }} />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 36 }}>
        <Button
          onClick={onPricingClick}
          data-testid="landing-pricing-teaser-see-full-pricing-button"
          style={primaryBtnLg}
        >
          See full pricing
          <ArrowRight size={18} style={{ marginLeft: 8 }} />
        </Button>
        <Button
          variant="outline"
          onClick={onTrialClick}
          data-testid="landing-pricing-teaser-start-trial-button"
          style={secondaryBtnLg}
        >
          Start free trial
        </Button>
        <Button
          variant="ghost"
          onClick={onDemoClick}
          data-testid="landing-pricing-teaser-book-demo-button"
          style={{ ...secondaryBtnLg, background: 'transparent', border: '1px dashed var(--kw-border)' }}
        >
          📅 Book a 15-min demo
        </Button>
      </div>
    </div>
  </section>
);

// ============================================================
// SECTION: Testimonials
// ============================================================

const Testimonials = () => (
  <section data-testid="landing-testimonials-section" style={{ padding: '80px 24px', background: '#ffffff' }}>
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 40 }}>
        <span style={sectionEyebrow}>What settings tell us</span>
        <h2 data-testid="landing-testimonials-title" style={sectionTitleStyle}>
          Calmer drop-offs. Happier parents. Less admin.
        </h2>
      </div>
      <div className="kw-testimonials-grid">
        {[
          { quote: 'Drop-off is calmer, staff spend less time on admin, and parents love the Moments feed.', name: 'Sarah K.', role: 'Nursery Manager · London', testid: 'landing-testimonial-1' },
          { quote: 'We switched in a week. The team helped us migrate and our staff picked it up quickly.', name: 'David M.', role: 'Director · Multi-site group', testid: 'landing-testimonial-2' },
          { quote: 'The recap videos at our open day genuinely impressed visiting families. Game changer.', name: 'Priya S.', role: 'Setting Owner · Manchester', testid: 'landing-testimonial-3' },
        ].map((t) => (
          <article key={t.testid} data-testid={t.testid} style={testimonialCardStyle}>
            <Quote size={20} style={{ color: 'var(--kw-cyan)', opacity: 0.8 }} />
            <p style={testimonialQuoteStyle}>“{t.quote}”</p>
            <div style={testimonialFooterStyle}>
              <div style={testimonialAvatarStyle}>{t.name[0]}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--kw-text)' }}>{t.name}</div>
                <div style={{ fontSize: 12, color: 'var(--kw-muted)' }}>{t.role}</div>
              </div>
            </div>
          </article>
        ))}
      </div>
      <div data-testid="landing-trust-badges" style={trustBadgesRow}>
        <span data-testid="landing-trust-badge-gdpr" style={trustBadgePill}>
          <BadgeCheck size={14} style={{ marginRight: 6, color: 'var(--kw-cyan)' }} /> GDPR-ready
        </span>
        <span data-testid="landing-trust-badge-ofsted" style={trustBadgePill}>
          <ShieldCheck size={14} style={{ marginRight: 6, color: 'var(--kw-cyan)' }} /> Ofsted-friendly workflows
        </span>
        <span data-testid="landing-trust-badge-uk" style={trustBadgePill}>
          <Heart size={14} style={{ marginRight: 6, color: 'var(--kw-cyan)' }} /> £ pricing · UK based
        </span>
      </div>
    </div>
  </section>
);

// ============================================================
// SECTION: FAQ
// ============================================================

const FAQS = [
  { q: 'Is Kids WiseApp GDPR compliant?', a: 'We support GDPR-friendly workflows and data handling. Every uploaded photo, attendance record and Moment is stored in tenant-scoped, encrypted-at-rest storage. We can also walk you through your DPIA during onboarding.', testid: 'landing-faq-gdpr' },
  { q: 'Is it suitable for Ofsted inspections?', a: 'Designed for UK settings with clear attendance, sign-in/out audit trails, ratios and safeguarding records that help you stay inspection-ready year-round.', testid: 'landing-faq-ofsted' },
  { q: 'How does the 14-day free trial work?', a: 'Start instantly on Starter, Growth or Professional. No card needed. Cancel anytime before the trial ends and you won’t be charged.', testid: 'landing-faq-trial' },
  { q: 'Can we switch from another system?', a: 'Yes. We help you migrate children, parents and staff data, train your team in a couple of sessions, and run alongside your existing system until you’re confident.', testid: 'landing-faq-switch' },
  { q: 'Do parents need to install an app?', a: 'Parents can use the web app for Moments, messaging and updates — no app store install required. Recap videos and event invites work via simple share links.', testid: 'landing-faq-parent-app' },
  { q: 'What support do you offer?', a: 'Email support on Starter, priority chat on Growth, phone support on Professional, and a dedicated CSM with SLA on Enterprise.', testid: 'landing-faq-support' },
];

const FAQ = () => (
  <section id="faq" data-testid="landing-faq-section" style={{ padding: '80px 24px', background: 'var(--kw-bg)' }}>
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <span style={sectionEyebrow}>FAQ</span>
        <h2 data-testid="landing-faq-title" style={sectionTitleStyle}>
          Questions we hear from UK settings.
        </h2>
      </div>
      <Accordion type="single" collapsible data-testid="landing-faq-accordion" style={{ background: '#fff', border: '1px solid var(--kw-border)', borderRadius: 'var(--kw-radius-md)', padding: '4px 18px' }}>
        {FAQS.map((f) => (
          <AccordionItem key={f.testid} value={f.testid} data-testid={f.testid}>
            <AccordionTrigger style={{ textAlign: 'left', color: 'var(--kw-text)', fontWeight: 600 }}>
              {f.q}
            </AccordionTrigger>
            <AccordionContent style={{ color: 'var(--kw-muted)', lineHeight: 1.6 }}>
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

// ============================================================
// SECTION: Final CTA
// ============================================================

const FinalCTA = ({ onTrialClick, onPricingClick, onDemoClick }) => (
  <section data-testid="landing-final-cta-section" style={{
    padding: '80px 24px',
    background: 'var(--kw-bg)',
    position: 'relative', overflow: 'hidden',
  }}>
    <div aria-hidden style={{
      position: 'absolute', inset: 0,
      background: 'var(--kw-hero-overlay)',
      pointerEvents: 'none',
    }} />
    <div style={{
      position: 'relative',
      maxWidth: 880, margin: '0 auto',
      background: '#fff',
      borderRadius: 'var(--kw-radius-lg)',
      border: '1px solid var(--kw-border)',
      boxShadow: 'var(--kw-shadow-lg)',
      padding: '48px 32px',
      textAlign: 'center',
    }}>
      <span style={{ ...sectionEyebrow, justifyContent: 'center', display: 'inline-flex', alignItems: 'center' }}>
        <Sparkles size={13} style={{ marginRight: 6 }} />
        Get started today
      </span>
      <h2 data-testid="landing-final-cta-title" style={{ ...sectionTitleStyle, marginTop: 14 }}>
        Ready to delight your parents — and reduce admin for your staff?
      </h2>
      <p style={{ ...sectionLeadStyle, maxWidth: 540, margin: '14px auto 0' }}>
        Start your free 14-day trial. No card required. Built for UK childcare.
      </p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 28 }}>
        <Button
          onClick={onTrialClick}
          data-testid="landing-final-cta-start-trial-button"
          style={primaryBtnLg}
        >
          Start free 14-day trial
          <ArrowRight size={18} style={{ marginLeft: 8 }} />
        </Button>
        <Button
          variant="outline"
          onClick={onPricingClick}
          data-testid="landing-final-cta-see-pricing-button"
          style={secondaryBtnLg}
        >
          See pricing
        </Button>
        <Button
          variant="ghost"
          onClick={onDemoClick}
          data-testid="landing-final-cta-book-demo-button"
          style={{ ...secondaryBtnLg, background: 'transparent', border: '1px dashed var(--kw-border)' }}
        >
          📅 Book a 15-min demo
        </Button>
      </div>
    </div>
  </section>
);

// ============================================================
// SECTION: Footer
// ============================================================

const Footer = () => (
  <footer data-testid="landing-footer" style={{
    background: '#ffffff',
    borderTop: '1px solid var(--kw-border)',
    padding: '56px 24px 32px',
  }}>
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div className="kw-footer-grid">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BrandLogo size={32} />
            <span style={{ ...brandWordmarkStyle, fontSize: 18 }}>
              Kids <span className="kw-gradient-text">WiseApp</span>
            </span>
          </div>
          <p style={{ marginTop: 12, fontSize: 13, color: 'var(--kw-muted)', maxWidth: 280 }}>
            The safeguarding-first childcare platform — made for UK settings.
          </p>
        </div>
        <div>
          <div style={footerColTitle}>Product</div>
          <ul style={footerLinkList}>
            <li><a href="#why" data-testid="landing-footer-link-why" style={footerLinkStyle}>Why Kids WiseApp</a></li>
            <li><a href="#features" data-testid="landing-footer-link-features" style={footerLinkStyle}>Features</a></li>
            <li><Link to="/pricing" data-testid="landing-footer-link-pricing" style={footerLinkStyle}>Pricing</Link></li>
          </ul>
        </div>
        <div>
          <div style={footerColTitle}>Company</div>
          <ul style={footerLinkList}>
            <li><a href="mailto:sales@kidswiseapp.com" data-testid="landing-footer-link-contact" style={footerLinkStyle}>Contact</a></li>
            <li><a href="mailto:support@kidswiseapp.com" data-testid="landing-footer-link-support" style={footerLinkStyle}>Support</a></li>
            <li><Link to="/login" data-testid="landing-footer-link-signin" style={footerLinkStyle}>Sign in</Link></li>
          </ul>
        </div>
        <div>
          <div style={footerColTitle}>Legal</div>
          <ul style={footerLinkList}>
            <li><Link to="/privacy" data-testid="landing-footer-link-privacy" style={footerLinkStyle}>Privacy</Link></li>
            <li><Link to="/terms" data-testid="landing-footer-link-terms" style={footerLinkStyle}>Terms</Link></li>
            <li><Link to="/cookies" data-testid="landing-footer-link-cookies" style={footerLinkStyle}>Cookies</Link></li>
          </ul>
        </div>
      </div>
      <div style={{
        marginTop: 36, paddingTop: 22,
        borderTop: '1px solid var(--kw-border)',
        display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
        gap: 12, fontSize: 12, color: 'var(--kw-muted)',
      }}>
        <span data-testid="landing-footer-copyright">© {new Date().getFullYear()} Kids WiseApp. Made for UK childcare settings.</span>
        <span>All prices in GBP. VAT charged where applicable.</span>
      </div>
    </div>
  </footer>
);

// ============================================================
// SECTION: Sticky mobile CTA
// ============================================================

const StickyMobileCTA = ({ onTrialClick, onDemoClick }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 480);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!show) return null;
  return (
    <div
      data-testid="landing-sticky-cta-bar"
      className="kw-show-mobile-flex"
      style={{
        display: 'none',
        position: 'fixed', left: 0, right: 0, bottom: 0,
        zIndex: 60,
        padding: '12px 16px calc(12px + env(safe-area-inset-bottom)) 16px',
        background: 'rgba(255,255,255,0.94)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid var(--kw-border)',
        boxShadow: '0 -8px 24px rgba(6,43,41,0.10)',
        gap: 10, alignItems: 'center',
      }}
    >
      <Link to="/pricing" data-testid="landing-sticky-cta-see-pricing-link" style={{ ...signinLinkStyle, fontSize: 14 }}>See pricing</Link>
      {onDemoClick && (
        <button
          type="button"
          onClick={onDemoClick}
          data-testid="landing-sticky-cta-book-demo-button"
          style={{ ...signinLinkStyle, fontSize: 14, background: 'none', border: 'none', cursor: 'pointer' }}
        >
          Demo
        </button>
      )}
      <Button
        onClick={onTrialClick}
        data-testid="landing-sticky-cta-start-trial-button"
        style={{ flex: 1, background: 'var(--kw-gradient-brand)', color: '#fff', fontWeight: 700, border: 'none', borderRadius: 12, boxShadow: '0 8px 20px rgba(0,171,201,0.28)' }}
      >
        Start free trial
        <ArrowRight size={16} style={{ marginLeft: 6 }} />
      </Button>
    </div>
  );
};

// ============================================================
// PAGE: LandingPage default export
// ============================================================

export default function LandingPage() {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  // Controls the "Book a 15-min demo" modal. `demoSource` records
  // which CTA opened it so PostHog attribution stays clean.
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoSource, setDemoSource] = useState('landing');
  const openDemo = (source) => { setDemoSource(source); setDemoOpen(true); };

  // Apply the marketing theme only while this page is mounted.
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-theme');
    const prevApp = document.body.getAttribute('data-theme');
    document.body.setAttribute('data-theme', 'marketing');
    // Fire once per mount so PostHog funnels see landing_viewed
    // separately from generic $pageview. Includes the referrer bucket
    // so we can slice: direct vs social vs paid vs partner referral.
    analytics.track(EVENTS.LANDING_VIEWED, {
      referrer: (typeof document !== 'undefined' && document.referrer) || null,
    });
    return () => {
      if (prevApp) document.body.setAttribute('data-theme', prevApp);
      else document.body.removeAttribute('data-theme');
      if (prev) document.documentElement.setAttribute('data-theme', prev);
    };
  }, []);

  const goTrial = () => {
    analytics.track(EVENTS.SIGNUP_STARTED, { source: 'landing' });
    navigate('/login?intent=trial');
  };
  const goPricing = () => {
    analytics.track(EVENTS.PRICING_VIEWED, { source: 'landing_cta' });
    navigate('/pricing');
  };

  return (
    <div ref={rootRef} data-theme="marketing" data-testid="landing-page-root" style={{ background: 'var(--kw-bg)', minHeight: '100vh' }}>
      <SEO
        title="Kids WiseApp — Childcare Management for After-School Clubs & Nurseries"
        description="Modern childcare ERP for after-school clubs, breakfast clubs, holiday programs and nurseries. Dynamic booking, digital chain of custody, staff operations, parent engagement, and instant invoicing — all in one place."
        canonical="https://kidswiseapp.com/"
        image="https://kidswiseapp.com/og-image.png"
      />
      {/* Responsive helpers — scoped <style> so we don't have to bloat global CSS */}
      <style>{`
        @media (min-width: 768px) {
          .kw-hide-mobile { display: flex !important; }
          .kw-hide-mobile-flex { display: flex !important; }
          .kw-show-mobile { display: none !important; }
          .kw-show-mobile-flex { display: none !important; }
        }
        @media (max-width: 767.99px) {
          .kw-hide-mobile { display: none !important; }
          .kw-hide-mobile-flex { display: none !important; }
          .kw-show-mobile { display: flex !important; }
          .kw-show-mobile-flex { display: flex !important; }
        }
        @media (min-width: 1024px) {
          .kw-hero-grid { grid-template-columns: 1.05fr 0.95fr !important; }
        }
        .kw-benefits-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
        }
        @media (min-width: 768px) {
          .kw-benefits-grid { grid-template-columns: repeat(2, 1fr); }
        }
        .kw-pricing-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 14px;
        }
        @media (min-width: 700px) {
          .kw-pricing-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .kw-pricing-grid { grid-template-columns: repeat(4, 1fr); }
        }
        .kw-testimonials-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 18px;
        }
        @media (min-width: 700px) { .kw-testimonials-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 1024px) { .kw-testimonials-grid { grid-template-columns: repeat(3, 1fr); } }
        .kw-feature-grid {
          grid-template-columns: 1fr !important;
        }
        @media (min-width: 900px) {
          .kw-feature-grid { grid-template-columns: 1fr 1fr !important; }
        }
        .kw-footer-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 28px;
        }
        @media (min-width: 700px) { .kw-footer-grid { grid-template-columns: 2fr 1fr 1fr 1fr; } }
      `}</style>

      <Navbar onTrialClick={goTrial} onPricingClick={goPricing} onDemoClick={() => openDemo('landing_navbar')} />
      <main>
        <Hero onTrialClick={goTrial} onPricingClick={goPricing} onDemoClick={() => openDemo('landing_hero')} />
        <SocialProof />
        <BenefitsBento />
        <FeatureSpotlights onPricingClick={goPricing} />
        <PricingTeaser onTrialClick={goTrial} onPricingClick={goPricing} onDemoClick={() => openDemo('landing_pricing_teaser')} />
        <Testimonials />
        <FAQ />
        <FinalCTA onTrialClick={goTrial} onPricingClick={goPricing} onDemoClick={() => openDemo('landing_final_cta')} />
      </main>
      <Footer />
      <StickyMobileCTA onTrialClick={goTrial} onDemoClick={() => openDemo('landing_sticky_cta')} />
      <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} source={demoSource} />
    </div>
  );
}

// ============================================================
// STYLES (inline tokens consumed everywhere above)
// ============================================================

const brandStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  textDecoration: 'none',
};
const brandIconStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 36, height: 36,
  borderRadius: 10,
  background: 'var(--kw-gradient-brand)',
  color: '#fff',
  boxShadow: 'var(--kw-shadow-brand)',
};
const brandWordmarkStyle = {
  fontFamily: "'Figtree', sans-serif",
  fontWeight: 800,
  fontSize: 19,
  color: 'var(--kw-text)',
  letterSpacing: '-0.015em',
};
const navLinksStyle = {
  display: 'none', gap: 30,
  fontSize: 14, fontWeight: 600,
  color: 'var(--kw-text-soft)',
};
const navLinkStyle = {
  textDecoration: 'none',
  color: 'inherit',
  padding: '6px 2px',
  position: 'relative',
  transition: 'color 180ms ease',
};
const signinLinkStyle = {
  fontSize: 14, fontWeight: 600,
  color: 'var(--kw-text-soft)',
  textDecoration: 'none',
  padding: '6px 10px',
};
const mobileMenuBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 42, height: 42, borderRadius: 12,
  background: 'transparent',
  border: '1px solid var(--kw-border-strong)',
  color: 'var(--kw-text)', cursor: 'pointer',
};
const mobileLinkStyle = {
  fontSize: 18, fontWeight: 600, color: 'var(--kw-text)',
  textDecoration: 'none',
  padding: '10px 0',
  borderBottom: '1px solid var(--kw-border)',
};

const eyebrowStyle = {
  display: 'inline-flex', alignItems: 'center',
  fontSize: 12, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.12em',
  color: 'var(--kw-cyan)',
  background: '#fff',
  border: '1px solid var(--kw-border)',
  padding: '7px 14px',
  borderRadius: 999,
  marginBottom: 18,
  boxShadow: 'var(--kw-shadow-sm)',
};
const h1Style = {
  fontFamily: "'Figtree', sans-serif",
  fontSize: 'clamp(2.4rem, 5.4vw, 4.2rem)',
  lineHeight: 1.04,
  fontWeight: 800,
  letterSpacing: '-0.025em',
  margin: '0 0 20px 0',
  color: 'var(--kw-text)',
};
const subStyle = {
  fontSize: 'clamp(1.05rem, 1.35vw, 1.22rem)',
  lineHeight: 1.55,
  color: 'var(--kw-muted)',
  maxWidth: 580,
  margin: 0,
};
const microcopyStyle = {
  marginTop: 14,
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--kw-muted)',
};
const trustBulletsStyle = {
  listStyle: 'none', padding: 0,
  margin: '26px 0 0 0',
  display: 'flex', flexWrap: 'wrap', gap: '10px 22px',
};
const trustBulletItem = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  fontSize: 13, fontWeight: 600,
  color: 'var(--kw-text)',
};
const trustBulletIcon = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22, borderRadius: 999,
  background: 'var(--kw-gradient-brand)',
  color: '#fff',
};

const primaryBtnLg = {
  background: 'var(--kw-gradient-brand)',
  color: '#fff',
  fontWeight: 700,
  fontSize: 15,
  padding: '15px 24px',
  height: 'auto',
  borderRadius: 14,
  border: 'none',
  boxShadow: 'var(--kw-shadow-brand)',
};
const secondaryBtnLg = {
  background: '#ffffff',
  color: 'var(--kw-text)',
  borderColor: 'var(--kw-border-strong)',
  borderWidth: 1.5,
  fontWeight: 700,
  fontSize: 15,
  padding: '15px 24px',
  height: 'auto',
  borderRadius: 14,
};

const sectionEyebrow = {
  display: 'inline-flex', alignItems: 'center',
  fontSize: 12, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.12em',
  color: 'var(--kw-cyan)',
  background: '#fff',
  border: '1px solid var(--kw-border)',
  padding: '7px 14px',
  borderRadius: 999,
  marginBottom: 16,
  boxShadow: 'var(--kw-shadow-sm)',
};
const sectionTitleStyle = {
  fontSize: 'clamp(1.9rem, 3.4vw, 2.8rem)',
  fontWeight: 800,
  lineHeight: 1.12,
  letterSpacing: '-0.02em',
  color: 'var(--kw-text)',
  margin: 0,
  maxWidth: 800,
};
const sectionLeadStyle = {
  marginTop: 16,
  fontSize: 17,
  lineHeight: 1.6,
  color: 'var(--kw-muted)',
  maxWidth: 720,
};

const benefitCardStyle = {
  background: '#fff',
  border: '1px solid var(--kw-border)',
  borderRadius: 'var(--kw-radius-md)',
  padding: '28px 24px',
  boxShadow: 'var(--kw-shadow-sm)',
  position: 'relative',
  overflow: 'hidden',
};
const benefitIconWrap = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 50, height: 50,
  borderRadius: 14,
  background: 'var(--kw-gradient-brand)',
  color: '#fff',
  marginBottom: 18,
  boxShadow: '0 8px 18px rgba(0,171,201,0.28)',
};
const benefitTitleStyle = {
  fontSize: 18, fontWeight: 700,
  color: 'var(--kw-text)',
  margin: '0 0 8px 0',
};
const benefitDescStyle = {
  fontSize: 14, lineHeight: 1.55,
  color: 'var(--kw-muted)',
  margin: 0,
};

const featureTitleStyle = {
  fontSize: 'clamp(1.4rem, 2.4vw, 1.9rem)',
  fontWeight: 800,
  color: 'var(--kw-text)',
  margin: '6px 0 16px 0',
  lineHeight: 1.2,
};
const featureBulletListStyle = {
  listStyle: 'none', padding: 0, margin: 0,
  display: 'flex', flexDirection: 'column', gap: 10,
};
const featureBulletItemStyle = {
  display: 'flex', alignItems: 'flex-start', gap: 10,
  fontSize: 15, color: 'var(--kw-text)',
};
const featureBulletCheckStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22,
  borderRadius: 999,
  background: 'var(--kw-cyan-soft)',
  color: 'var(--kw-cyan)',
  flexShrink: 0,
};
const featureVisualStyle = {
  borderRadius: 'var(--kw-radius-lg)',
  border: '1px solid var(--kw-border)',
  boxShadow: 'var(--kw-shadow-md)',
  padding: 44,
  minHeight: 280,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
};
const featureVisualInner = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18,
  color: 'var(--kw-cyan)',
};
const featureVisualIconWrap = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 96, height: 96,
  borderRadius: 28,
  background: 'var(--kw-gradient-brand)',
  color: '#fff',
  boxShadow: 'var(--kw-shadow-brand)',
};
const featureVisualLabel = {
  fontSize: 12, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.14em',
  color: 'var(--kw-text)',
  background: '#fff',
  padding: '7px 14px',
  borderRadius: 999,
  boxShadow: 'var(--kw-shadow-sm)',
};

const inlineCtaBlockStyle = {
  display: 'flex', alignItems: 'center', gap: 14,
  flexWrap: 'wrap',
  background: 'var(--kw-surface-2)',
  border: '1px solid var(--kw-border)',
  borderRadius: 'var(--kw-radius-md)',
  padding: '14px 18px',
  marginBottom: 64,
};

const pricingCardStyle = {
  position: 'relative',
  background: '#fff',
  border: '1px solid var(--kw-border)',
  borderRadius: 'var(--kw-radius-md)',
  padding: '24px 20px',
  boxShadow: 'var(--kw-shadow-sm)',
  display: 'flex', flexDirection: 'column',
};
const pricingCardRecommendedStyle = {
  borderColor: 'transparent',
  background: 'linear-gradient(#ffffff,#ffffff) padding-box, var(--kw-gradient-brand) border-box',
  border: '2px solid transparent',
  boxShadow: '0 24px 50px rgba(0,171,201,0.22)',
  transform: 'translateY(-6px)',
};
const recommendedBadge = {
  position: 'absolute', top: -14, right: 14,
  display: 'inline-flex', alignItems: 'center',
  background: 'var(--kw-gradient-brand)',
  color: '#fff',
  fontSize: 11, fontWeight: 800,
  letterSpacing: '0.06em', textTransform: 'uppercase',
  padding: '6px 12px',
  borderRadius: 999,
  boxShadow: '0 8px 18px rgba(0,171,201,0.32)',
};
const pricingTierName = {
  fontSize: 14, fontWeight: 700,
  color: 'var(--kw-muted)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  marginBottom: 6,
};
const pricingTierPrice = {
  fontSize: 30, fontWeight: 800,
  color: 'var(--kw-text)',
  letterSpacing: '-0.01em',
  marginBottom: 12,
};
const pricingTierPriceSuffix = {
  fontSize: 14, fontWeight: 600,
  color: 'var(--kw-muted)',
};
const pricingTierHighlightList = {
  listStyle: 'none', padding: 0, margin: 0,
  display: 'flex', flexDirection: 'column', gap: 8,
  fontSize: 13,
};
const pricingTierHighlightItem = {
  display: 'flex', alignItems: 'flex-start',
  color: 'var(--kw-text)',
  lineHeight: 1.5,
};

const testimonialCardStyle = {
  background: '#fff',
  border: '1px solid var(--kw-border)',
  borderRadius: 'var(--kw-radius-md)',
  padding: '22px 22px 18px',
  boxShadow: 'var(--kw-shadow-sm)',
  display: 'flex', flexDirection: 'column', gap: 14,
};
const testimonialQuoteStyle = {
  fontSize: 15, lineHeight: 1.6,
  color: 'var(--kw-text)',
  margin: 0,
};
const testimonialFooterStyle = {
  display: 'flex', alignItems: 'center', gap: 12,
  marginTop: 'auto',
  paddingTop: 12,
  borderTop: '1px dashed var(--kw-border)',
};
const testimonialAvatarStyle = {
  width: 36, height: 36,
  borderRadius: '50%',
  background: 'var(--kw-cyan-soft)',
  color: 'var(--kw-cyan)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontWeight: 800, fontSize: 14,
};
const trustBadgesRow = {
  display: 'flex', justifyContent: 'center', flexWrap: 'wrap',
  gap: 12, marginTop: 36,
};
const trustBadgePill = {
  display: 'inline-flex', alignItems: 'center',
  background: '#fff',
  border: '1px solid var(--kw-border)',
  borderRadius: 999,
  padding: '8px 14px',
  fontSize: 13, fontWeight: 600,
  color: 'var(--kw-text)',
  boxShadow: 'var(--kw-shadow-sm)',
};

const footerColTitle = {
  fontSize: 12, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.1em',
  color: 'var(--kw-muted)',
  marginBottom: 12,
};
const footerLinkList = {
  listStyle: 'none', padding: 0, margin: 0,
  display: 'flex', flexDirection: 'column', gap: 10,
};
const footerLinkStyle = {
  fontSize: 14, color: 'var(--kw-text)',
  textDecoration: 'none',
};

// Browser-mockup styles
const browserFrameStyle = {
  width: '100%', maxWidth: 560,
  borderRadius: 18,
  background: '#fff',
  border: '1px solid var(--kw-border)',
  boxShadow: '0 30px 60px rgba(6,43,41,0.18)',
  overflow: 'hidden',
};
const browserChromeStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 14px',
  background: '#f0f6f5',
  borderBottom: '1px solid var(--kw-border)',
};
const browserDot = {
  width: 11, height: 11, borderRadius: '50%',
  display: 'inline-block',
};
const browserAddressBar = {
  marginLeft: 12,
  fontSize: 11,
  color: 'var(--kw-muted)',
  background: '#fff',
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid var(--kw-border)',
};
const browserContentStyle = {
  display: 'grid', gridTemplateColumns: '120px 1fr',
  minHeight: 320,
};
const mockSidebarStyle = {
  borderRight: '1px solid var(--kw-border)',
  padding: '14px 10px',
  display: 'flex', flexDirection: 'column', gap: 6,
  background: '#fafdfc',
};
const mockSidebarBrand = {
  display: 'flex', alignItems: 'center', gap: 6,
  fontSize: 11, fontWeight: 800, color: 'var(--kw-text)',
  marginBottom: 10,
};
const mockSidebarItem = {
  fontSize: 10.5,
  color: 'var(--kw-muted)',
  padding: '6px 8px',
  borderRadius: 6,
};
const mockSidebarItemActive = {
  background: 'var(--kw-cyan-soft)',
  color: 'var(--kw-cyan)',
  fontWeight: 700,
};
const mockMainStyle = {
  padding: '14px 16px',
  display: 'flex', flexDirection: 'column', gap: 10,
};
const mockHeaderRow = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10,
};
const mockH2 = { fontSize: 14, fontWeight: 800, color: 'var(--kw-text)' };
const mockSubtle = { fontSize: 10, color: 'var(--kw-muted)' };
const mockBadgeOk = {
  display: 'inline-flex', alignItems: 'center',
  fontSize: 10, fontWeight: 700,
  background: '#dbf5ef', color: '#0b6b58',
  padding: '4px 8px', borderRadius: 999,
};
const mockStatsRow = {
  display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8,
};
const mockStatCard = {
  background: '#fff',
  border: '1px solid var(--kw-border)',
  borderRadius: 10,
  padding: '8px 10px',
};
const mockStatLabel = { fontSize: 9.5, color: 'var(--kw-muted)' };
const mockRosterRow = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '6px 8px',
  background: '#fff',
  border: '1px solid var(--kw-border)',
  borderRadius: 8,
};
const mockAvatar = {
  width: 26, height: 26, borderRadius: '50%',
  background: 'var(--kw-cyan-soft)', color: 'var(--kw-cyan)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11, fontWeight: 800,
};
const mockPillIn = {
  fontSize: 9.5, fontWeight: 700, color: '#0b6b58',
  background: '#dbf5ef', padding: '3px 8px', borderRadius: 999,
};
const mockPillOut = {
  fontSize: 9.5, fontWeight: 700, color: '#446f6b',
  background: '#eaf1f0', padding: '3px 8px', borderRadius: 999,
};
